import { PautaEvento } from 'api/Assessor/pautas';
import {
  AssembleiaIcon,
  CircleTimes,
  PersonGraph,
  SyncIcon,
} from 'components/Icons';
import AbrirVotacaoPautaModal, {
  IAbrirVotacaoPautaModalGenericProps,
} from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/AbrirVotacaoPautaModal';
import AcompanharVotacaoPautaModal, {
  IAcompanharVotacaoPautaModalGenericProps,
} from 'components/Modal/__LEGACY__/AcompanharVotacaoPautaModal/AcompanharVotacaoPautaModal';
import { AssembleiaPautaStatus } from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import { Button } from 'plataforma-fundacao-componentes';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assembleiasAssessorActionsAlterarSituacaoPauta } from 'store/modules/AssembleiasAssessorActions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import ComputacaoVotoAction from './ComputacaoVoto';
import EditionActions from './Edition';
import LiberacaoResultadoActions from './LiberacaoResultado';
import VotoPresencialAction from './VotoPresencial';

interface Props {
  pauta: PautaEvento;
  blockedUntilQuorum?: boolean;
  currentPage: number;
  onDelete: () => void;
}

export default function AssessorPautaActions({
  pauta,
  blockedUntilQuorum,
  currentPage,
  onDelete,
}: Props) {
  const dispatch = useDispatch();
  const { evento } = useSelector(
    (state: IReducer) => state.assembleiasAssessor
  );
  const usuario = useSelector((state: IReducer) => state.auth.usuario);
  const [sendingAlteracaoStatus, setSendingAlteracaoStatus] =
    useState<boolean>(false);

  const encerrarVotacao = useCallback(() => {
    setSendingAlteracaoStatus(true);
    dispatch(
      assembleiasAssessorActionsAlterarSituacaoPauta(
        Number(evento?.id),
        pauta.idPauta,
        null,
        0,
        AssembleiaPautaStatus.Encerrada,
        () => setSendingAlteracaoStatus(false),
        (error: any) => ErrorHandler(error.response!.data)
      )
    );
  }, [dispatch, evento?.id, pauta.idPauta]);

  const abrirVotacao = useCallback(() => {
    const modalKey: string = getUniqueKey();
    if (pauta!.status === AssembleiaPautaStatus.Bloqueada) {
      dispatch(
        modalsActionsOpen(
          AbrirVotacaoPautaModal,
          {
            genericProps: {
              key: modalKey,
              emLote: true,
              idEvento: evento!.id,
              pautas: [pauta!.idPauta],
            } as IAbrirVotacaoPautaModalGenericProps,
          },
          modalKey
        )
      );
    } else {
      dispatch(
        modalsActionsOpen(
          AbrirVotacaoPautaModal,
          {
            genericProps: {
              key: modalKey,
              idEvento: evento!.id,
              idPauta: pauta!.idPauta,
            } as IAbrirVotacaoPautaModalGenericProps,
          },
          modalKey
        )
      );
    }
  }, [dispatch, evento, pauta]);

  const acompanharVotacao = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        AcompanharVotacaoPautaModal,
        {
          genericProps: {
            key: modalKey,
            pauta,
            idPauta: pauta.idPauta,
          } as IAcompanharVotacaoPautaModalGenericProps,
        },
        modalKey
      )
    );
  }, [dispatch, pauta]);

  const trocaStatusOnClick = useCallback(() => {
    switch (pauta.status) {
      case AssembleiaPautaStatus.Liberada:
        return encerrarVotacao();
      default:
        return abrirVotacao();
    }
  }, [pauta, encerrarVotacao, abrirVotacao]);

  const trocaStatusLabel = useMemo(() => {
    switch (pauta.status) {
      case AssembleiaPautaStatus.Bloqueada:
        return translation.AssembleiaPauta.acoes.liberarVotacao;
      case AssembleiaPautaStatus.Liberada:
        return translation.AssembleiaPauta.acoes.encerrarVotacao;
      case AssembleiaPautaStatus.Encerrada:
      default:
        return translation.AssembleiaPauta.acoes.reabrirVotacao;
    }
  }, [pauta]);

  const StatusIcon: React.FC<{ status: AssembleiaPautaStatus }> = ({
    status,
  }) => {
    if (status === AssembleiaPautaStatus.Liberada) {
      return <CircleTimes />;
    }
    return <AssembleiaIcon />;
  };

  if (!evento || !usuario) {
    return null;
  }

  return (
    <>
      {!pauta.paginaPresencial ? (
        <Fragment>
          <EditionActions
            pauta={pauta}
            evento={evento}
            usuario={usuario}
            currentPage={currentPage}
            onDelete={onDelete}
          />
          {pauta.status === AssembleiaPautaStatus.Liberada ||
          pauta.resultadoLiberado ? (
            <Button
              data-testid={'assessor-pauta-visualizar-acompanhar'}
              onClick={acompanharVotacao}
              leftIcon={
                sendingAlteracaoStatus ? <SyncIcon animated /> : <PersonGraph />
              }
            >
              {pauta.status === AssembleiaPautaStatus.Encerrada
                ? translation.AssembleiaPauta.acoes.visualizarResultados
                : translation.AssembleiaPauta.acoes.acompanharVotacao}
            </Button>
          ) : undefined}
          <VotoPresencialAction
            pauta={pauta}
            evento={evento}
            usuario={usuario}
          />
          <LiberacaoResultadoActions pauta={pauta} evento={evento} />
          {((usuario.perfil.id === 'pertencer_consulta' &&
            !usuario.perfil.assessorRoute.assembleias.pautas.alterarSituacao &&
            evento?.tipo === 'REU') ||
            (usuario.perfil.id !== 'pertencer_consulta' &&
              usuario.perfil.assessorRoute.assembleias.pautas
                .alterarSituacao)) &&
            pauta.itens.length > 0 &&
            !pauta.resultadoLiberadoAssociado &&
            evento.status === AssembleiaStatus.Live &&
            evento.tipo !== AssembleiaTipo.Nucleo &&
            evento.tipo !== AssembleiaTipo.Reuniao && (
              <>
                <Button
                  data-testid={'assessor-pauta-liberar-encerrar-reabrir'}
                  onClick={trocaStatusOnClick}
                  leftIcon={
                    sendingAlteracaoStatus ? (
                      <SyncIcon animated />
                    ) : (
                      <StatusIcon status={pauta.status} />
                    )
                  }
                  disabled={blockedUntilQuorum}
                >
                  {trocaStatusLabel}
                </Button>
              </>
            )}
          {((usuario.perfil.id === 'pertencer_consulta' &&
            !usuario.perfil.assessorRoute.assembleias.pautas.alterarSituacao &&
            evento?.tipo === 'REU') ||
            (usuario.perfil.id !== 'pertencer_consulta' &&
              usuario.perfil.assessorRoute.assembleias.pautas
                .alterarSituacao)) &&
            pauta.itens.length > 0 &&
            !pauta.resultadoLiberadoAssociado &&
            evento.status === AssembleiaStatus.Live &&
            (evento.tipo === AssembleiaTipo.Nucleo ||
              evento.tipo === AssembleiaTipo.Reuniao) &&
            evento.flagIniciarVotacaoDigital && (
              <>
                <Button
                  data-testid={'assessor-pauta-liberar-encerrar-reabrir'}
                  onClick={trocaStatusOnClick}
                  leftIcon={
                    sendingAlteracaoStatus ? (
                      <SyncIcon animated />
                    ) : (
                      <StatusIcon status={pauta.status} />
                    )
                  }
                  disabled={blockedUntilQuorum}
                >
                  {trocaStatusLabel}
                </Button>
              </>
            )}
        </Fragment>
      ) : (
        <Fragment>
          <ComputacaoVotoAction
            pauta={pauta}
            evento={evento}
            usuario={usuario}
          ></ComputacaoVotoAction>
        </Fragment>
      )}
    </>
  );
}
